<template>
  <div class="contact-form">
    <form v-if="!sent" @submit.prevent="handleSubmit">
      <div class="form-field">
        <label class="form-label">{{ $t('Your name') }}</label>
        <input v-model="form.name" type="text" class="form-input" required />
      </div>

      <div class="form-field">
        <label class="form-label">{{ $t('Email') }}</label>
        <input v-model="form.email" type="email" class="form-input" required />
      </div>

      <div class="form-field">
        <label class="form-label">{{ $t('Subject') }}</label>
        <input v-model="form.subject" type="text" class="form-input" required />
      </div>

      <div class="form-field">
        <label class="form-label">{{ $t('Message') }}</label>
        <textarea v-model="form.message" rows="5" class="form-input form-textarea" required />
      </div>

      <div v-if="error" class="alert alert--error">
        {{ error }}
      </div>

      <button
        type="submit"
        :disabled="loading"
        class="btn btn-primary btn-size-l contact-form__submit"
      >
        {{ loading ? $t('Sending...') : $t('Send Message') }}
      </button>
    </form>

    <div v-else class="alert alert--success alert--success-large">
      <p class="alert__title">{{ $t('Message sent!') }}</p>
      <p class="alert__text">{{ $t("We'll get back to you as soon as possible.") }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  reasonSubject?: string
  recipientType?: 'support' | 'partner'
}>()

const { $executeRecaptcha, $i18n } = useNuxtApp()

const form = reactive({
  name: '',
  email: '',
  subject: props.reasonSubject || '',
  message: '',
})

const loading = ref(false)
const error = ref('')
const sent = ref(false)

async function handleSubmit() {
  loading.value = true
  error.value = ''

  try {
    const recaptchaToken = await $executeRecaptcha('support_contact')
    const body: Record<string, string> = {
      name: form.name,
      email: form.email,
      subject: form.subject,
      message: form.message,
      recaptcha_token: recaptchaToken,
    }
    if (props.recipientType) {
      body.recipient_type = props.recipientType
    }
    await $fetch('/api/order/v3/support/contact', {
      method: 'POST',
      body,
    })
    sent.value = true
  } catch (e: any) {
    if (e?.response?.status === 403 || e?.statusCode === 403) {
      error.value = $i18n.t('Verification failed. Please try again.')
    } else {
      error.value = $i18n.t('Something went wrong. Please try again later.')
    }
  } finally {
    loading.value = false
  }
}
</script>

<style lang="scss" scoped>
.contact-form {
  &__submit {
    width: 100%;
  }
}

.form-field {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--black);
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  border: 1px solid var(--grey-border);
  border-radius: 6px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: var(--blue);
    box-shadow: 0 0 0 2px var(--blue-highlight, rgba(25, 133, 249, 0.15));
  }

  &::placeholder {
    color: var(--dark-grey-secondary, #838383);
  }
}

.form-textarea {
  resize: vertical;
  min-height: 120px;
}

.alert {
  padding: 14px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;

  &--error {
    background: #FFF3F3;
    border: 1px solid #E57373;
    color: #C62828;
  }

  &--success {
    background: #E8F5E9;
    border: 1px solid #81C784;
  }

  &--success-large {
    text-align: center;
    padding: 32px 24px;
  }

  &__title {
    font-weight: 600;
    font-size: 18px;
    color: #2E7D32;
    margin: 0 0 4px;
  }

  &__text {
    color: #388E3C;
    font-size: 14px;
    margin: 0;
  }
}
</style>
